import { useSearchParams } from "react-router-dom";
import { Create, DateInput, SelectInput, SimpleForm, TextInput, useRedirect } from "react-admin";
import { useFormContext } from "react-hook-form";
import { Alert, Typography } from "@mui/material";
import dayjs from "dayjs";

import NoArrowKeyNumberInput from "@/components/NoArrowKeyNumberInput";
import { navigateToBaseRoute, parsePathname } from "@/utils/route-utility";
import MiqaatNiyaazItLookup from "@/containers/yearly-ledger/components/MiqaatNiyaazItLookup";
import MiqaatNiyaazPaymentModeInput from "@/containers/yearly-ledger/components/MiqaatNiyaazPaymentModeInput";

const getCurrentFinancialYearRange = () => {
  const now = new Date();
  const month = now.getUTCMonth() + 1;
  const year = now.getUTCFullYear();
  const startYear = month >= 4 ? year : year - 1;
  const endYear = startYear + 1;
  return {
    start: new Date(Date.UTC(startYear, 3, 1, 0, 0, 0, 0)),
    end: new Date(Date.UTC(endYear, 2, 31, 23, 59, 59, 999)),
  };
};

const validateReceiptDateInCurrentFinancialYear = (value?: string | Date) => {
  if (!value) return "Receipt date is required";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "Invalid receipt date";

  const { start, end } = getCurrentFinancialYearRange();
  if (date < start || date > end) {
    return "Only current financial year receipts are allowed";
  }
  return undefined;
};

const ReceiptTypeDependentFields = () => {
  const { watch } = useFormContext();
  const receiptType = watch("receiptType");
  return (
    <>
      {receiptType === "CREDIT" && <MiqaatNiyaazItLookup />}
      <TextInput source="name" label="Name" fullWidth isRequired />
      {receiptType === "DEBIT" && (
        <TextInput source="itsNo" label="ITS No (Optional - for external vendors)" fullWidth />
      )}
    </>
  );
};

type YearlyLedgerCreateProps = {
  yearlyLedgerTypeId?: string | null;
  ledgerTypeName?: string | null;
};

export default function YearlyLedgerCreate({
  yearlyLedgerTypeId,
  ledgerTypeName,
}: YearlyLedgerCreateProps) {
  const [sp] = useSearchParams();
  const redirect = useRedirect();
  const typeId = yearlyLedgerTypeId ?? sp.get("yearlyLedgerTypeId");
  const typeName = ledgerTypeName ?? sp.get("ledgerTypeName");
  const financialYearFromQuery = sp.get("financialYear");

  const redirectToDashboard = () => {
    const params = new URLSearchParams();
    if (typeId) params.set("yearlyLedgerTypeId", typeId);
    if (financialYearFromQuery) {
      params.set("financialYear", financialYearFromQuery);
    } else {
      const now = dayjs();
      const month = now.month() + 1;
      const year = now.year();
      const fy = month >= 4 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
      params.set("financialYear", fy);
    }

    if (parsePathname().baseRoute !== "yearlyLedger") {
      navigateToBaseRoute("yearlyLedger");
    }
    redirect(`/?${params.toString()}`);
  };

  if (!typeId) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        No ledger type selected. Open the dashboard and choose a ledger before creating an entry.
      </Alert>
    );
  }

  return (
    <Create
      redirect={false}
      mutationOptions={{
        onSuccess: redirectToDashboard,
      }}
      transform={(data: Record<string, unknown>) => ({
        yearlyLedgerTypeId: typeId,
        receiptType: data.receiptType,
        receiptDate: data.receiptDate,
        paymentMode: data.paymentMode || "CASH",
        amount: data.amount,
        purpose: data.purpose || null,
        receiptNo: data.receiptNo || null,
        itsNo: data.itsNo || null,
        name: data.name || null,
        paymentRef: data.paymentRef || null,
        remarks: data.remarks || null,
      })}
    >
      <SimpleForm
        warnWhenUnsavedChanges
        sx={{ maxWidth: 700 }}
        defaultValues={{
          receiptDate: new Date(),
          receiptType: "DEBIT",
          paymentMode: "CASH",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Ledger: {typeName || typeId}
        </Typography>
        <SelectInput
          source="receiptType"
          label="Entry Type"
          choices={[
            { id: "CREDIT", name: "CREDIT (Income)" },
            { id: "DEBIT", name: "DEBIT (Expense)" },
          ]}
          fullWidth
          isRequired
        />
        <ReceiptTypeDependentFields />
        <TextInput source="purpose" label="Purpose" fullWidth isRequired />
        <TextInput source="receiptNo" label="Receipt No (Optional)" fullWidth />
        <NoArrowKeyNumberInput source="amount" label="Amount" fullWidth isRequired />
        <DateInput
          source="receiptDate"
          label="Receipt Date"
          fullWidth
          isRequired
          validate={validateReceiptDateInCurrentFinancialYear}
        />
        <MiqaatNiyaazPaymentModeInput />
        <TextInput source="remarks" label="Remarks" fullWidth multiline rows={3} />
      </SimpleForm>
    </Create>
  );
}
